<template>
  <NuxtLink v-if="!video" class="app-community-card-item" :to="url" :target="isSameOriginUrl(url) ? '_self' : '_blank'">
    <AspectRatio class="overflow-hidden" :ratio="ratio">
      <NuxtImg
        class="app-community-card-img w-full h-full object-cover transition-all duration-500"
        :src="image"
        :alt="title"
        format="webp"
        quality="80"
      />
    </AspectRatio>
    <div class="app-community-card-ctx">
      <h4 class="app-community-card-title truncate ...">{{ title }}</h4>
      <div v-if="description" class="app-community-card-desc">{{ description }}</div>
    </div>
  </NuxtLink>
  <CommonVideoPopup v-else :url="url">
    <div class="app-community-card-item cursor-pointer">
      <AspectRatio class="overflow-hidden" :ratio="ratio">
        <NuxtImg
          class="app-community-card-img w-full h-full object-cover transition-all duration-500"
          :src="image"
          :alt="title"
          format="webp"
          quality="80"
        />
      </AspectRatio>
      <div class="app-community-card-ctx">
        <h4 class="app-community-card-title truncate ...">{{ title }}</h4>
        <div v-if="description" class="app-community-card-desc">{{ description }}</div>
      </div>
      <div class="app-community-card-play transition-colors duration-500">
        <IconsPlay class="w-full h-full max-lg:scale-[1.4]" />
      </div>
      <!--  -->
      <div v-show="videoTag" class="app-community-card-video-tag">{{ videoTag }}</div>
      <div class="app-community-card-like">
        <div class="app-community-card-like-icon" @click.stop="likeIconClick">
          <IconsLike :type="isLiked ? 'liked' : 'default'" />
        </div>
        <div class="app-community-card-like-nums" v-if="`${likeCount}` !== '0'">
          <span class="app-community-card-like-nums-text" :class="{ 'text-primary': isLiked }">{{ likeCount }}</span>
        </div>
      </div>
    </div>
  </CommonVideoPopup>
</template>

<script setup lang="ts">
import { isSameOriginUrl } from '~/utils'
const emit = defineEmits(['lickeIconClickEmit'])
defineProps({
  title: {
    type: String,
    required: true
  },
  image: {
    type: String,
    required: true
  },
  description: {
    type: String,
    default: ''
  },
  url: {
    type: String,
    default: ''
  },
  videoTag: {
    type: String,
    default: ''
  },
  isLiked: {
    type: Boolean,
    default: false
  },
  likeCount: {
    type: String,
    default: '0'
  },
  video: Boolean
})
const ratio = 464 / 262
// Emit event 'lickeIconClickEmit' when like icon is clicked

function likeIconClick() {
  emit('lickeIconClickEmit')
}
</script>

<style scoped lang="scss">
.app-community-card-item {
  @apply relative block;
}
.app-community-card-ctx {
  @apply absolute bottom-0 left-0 right-0 py-2 flex flex-col w-full; // all
  @apply ltr:pl-4 rtl:pr-4;
  @apply max-lg:py-1 ltr:max-lg:pl-2;
  @apply ltr:lg:pr-17 ltr:max-lg:landscape:pr-12 ltr:max-lg:portrait:pr-13 ltr:rtl:pr-2;
  @apply rtl:lg:pl-17 rtl:max-lg:landscape:pl-12 rtl:max-lg:portrait:pl-13 rtl:ltr:pl-2;
  // Figma: linear-gradient(0deg, rgba(0,0,0,1) 16%, rgba(0,0,0,0) 100%)
  background: linear-gradient(0deg, rgba(0, 0, 0, 1) 16%, rgba(0, 0, 0, 0) 100%);
}
.app-community-card-title {
  @apply relative text-base font-medium text-white px-3;
  @apply before:content-[''] before:bg-primary-color before:w-1 before:h-4 before:absolute before:top-1  ltr:before:left-0 rtl:before:right-0;
  @apply max-lg:px-1.5 max-lg:text-xs max-lg:leading-4.5 max-lg:before:h-2 max-lg:before:w-0.5 max-lg:before:top-[5px];
}
.app-community-card-desc {
  @apply relative text-[#d1d1d1] text-m-p px-3 mt-1 max-lg:mt-0.5 max-lg:px-1.5;
  @apply before:content-[''] before:bg-light before:w-1 before:h-1 before:absolute before:top-2;
  @apply max-lg:text-xs max-lg:leading-3.5 max-lg:font-normal max-lg:before:top-[5px];
  @apply ltr:before:left-0 rtl:before:right-0;
}
.app-community-card-play {
  @apply absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden;
  @apply w-11 h-11 bg-dark-background rounded-full flex items-center justify-center text-white;
  @apply max-lg:w-8 max-lg:h-8 max-lg:landscape:w-6 max-lg:landscape:h-6;
}
@media not all and (hover: none) {
  .app-community-card-item:hover {
    .app-community-card-play {
      @apply text-primary;
    }
    .app-community-card-img {
      @apply scale-105;
    }
  }
}
.app-community-card-video-tag {
  @apply absolute  lg:top-2 max-lg:landscape:top-1 portrait:top-1.5 text-white;
  @apply ltr:left-0;
  @apply rtl:right-0;
  @apply lg:text-[12px]/[20px] max-lg:text-[10px]/[14px];
  @apply lg:py-0.5   max-lg:portrait:py-1 max-lg:landscape:py-0.5;
  @apply ltr:lg:pr-2 ltr:lg:pl-4 ltr:max-lg:pr-1.5 ltr:max-lg:pl-3;
  @apply rtl:lg:pl-2 rtl:lg:pr-4 rtl:max-lg:pl-1.5 rtl:max-lg:pr-3;
  @apply bg-[#363A41];
  @apply before:content-[''] before:absolute before:bg-primary;
  @apply before:lg:w-1 before:lg:h-1 before:max-lg:w-0.5 before:max-lg:h-0.5;
  @apply before:top-1/2 before:translate-x-[-50%];
  @apply before:ltr:lg:left-2 before:rtl:lg:right-2;
  @apply before:ltr:max-lg:left-1.5 before:rtl:max-lg:right-1.5;
}

.app-community-card-like {
  @apply absolute;
  @apply lg:w-[52px] lg:h-[44px] max-lg:w-[40px] max-lg:h-[32px];
  @apply ltr:lg:right-4 lg:bottom-3;
  @apply rtl:lg:left-4;
  @apply ltr:max-lg:landscape:right-1 max-lg:landscape:bottom-[5px];
  @apply rtl:max-lg:landscape:left-1;
  @apply ltr:max-lg:portrait:right-2 max-lg:portrait:bottom-[5px];
  @apply rtl:max-lg:portrait:left-2;
}
.app-community-card-like-icon {
  @apply pointer-events-auto;
  @apply absolute ltr:left-0 rtl:right-0 bottom-0;
  @apply lg:w-10 lg:h-10 max-lg:w-7 max-lg:h-7;
  @apply hover:text-primary;
  @apply transition-colors duration-500;
  svg {
    @apply w-full h-full;
  }
}
.app-community-card-like-nums {
  @apply pointer-events-none;
  // @apply absolute top-0 left-[calc(50%+4px)] -translate-x-1/2 px-1.5;
  @apply absolute top-0;
  @apply ltr:lg:left-[33px] rtl:lg:right-[33px];
  @apply ltr:max-lg:left-[26px] rtl:max-lg:right-[26px];
  @apply ltr:-translate-x-1/2 rtl:translate-x-1/2;
  @apply lg:px-1.5 max-lg:px-1;
  @apply min-w-[17px];
  @apply text-center;
  @apply text-[#D1D1D1];
  @apply hover:text-primary;
  @apply lg:text-[12px]/[16px] max-lg:text-[10px]/[12px];
  @apply lg:leading-[16px] max-lg:leading-[12px];
  @apply transition-colors duration-500;
  @apply bg-[rgba(20,20,20,0.7)] rounded-[8px];
}
.app-community-card-like-nums-text {
  @apply relative z-20;
}
</style>
